import { useContext, useMemo, type MouseEvent } from 'react';
import { getCurrentProject } from '@/lib/current-project';
import { linkDialogBaseSlug, openPageDialog, openWebDialog } from './link-dialog-store';
import { LinkScopeContext } from './link-scope-context';
import { canFrame, canOpenInDialog, internalPath, isModifiedClick } from './link-targets';

export interface LinkOpener {
  /** Links open in dialogs here. */
  active: boolean;
  /**
   * For a factory link's click: true when it stays on this page (it opened the dialog, or moves the
   * dialog on), so the caller shouldn't treat it as leaving (a task pop-up's onNavigate). False:
   * an ordinary link (a new tab, or a page that can't open in the dialog).
   */
  onInternalClick: (path: string, event: MouseEvent<HTMLAnchorElement>) => boolean;
  /** For a web address's click: opens the framed dialog where the site allows it, else leaves the new tab to the link. */
  onExternalClick: (href: string, event: MouseEvent<HTMLAnchorElement>) => void;
  /** The link opens over this page (the dialog), so unsaved work here needn't stop it (`data-keeps-page`). */
  keepsPage: (path: string) => boolean;
  /** A full address on this site, as a path to link to in the app; null for anywhere else, or when links open as links. */
  ownPath: (href: string) => string | null;
}

export function useLinkOpener(): LinkOpener {
  const scope = useContext(LinkScopeContext);
  return useMemo<LinkOpener>(
    () => ({
      active: scope !== 'page',
      onInternalClick: (path, event) => {
        if (scope === 'page' || isModifiedClick(event)) return false;
        // The link moves the dialog's own router on; it checks the page can show there.
        if (scope === 'inDialog') return true;
        const baseSlug = linkDialogBaseSlug(getCurrentProject()?.slug ?? null);
        if (!canOpenInDialog(path, baseSlug)) return false;
        event.preventDefault();
        void openPageDialog(path, baseSlug);
        return true;
      },
      onExternalClick: (href, event) => {
        if (scope === 'page' || isModifiedClick(event) || !canFrame(href)) return;
        event.preventDefault();
        openWebDialog(href);
      },
      keepsPage: (path) => scope === 'dialog' && canOpenInDialog(path, linkDialogBaseSlug(getCurrentProject()?.slug ?? null)),
      ownPath: (href) => (scope === 'page' ? null : internalPath(href, window.location.origin)),
    }),
    [scope]
  );
}
